import React from "react";

interface Caracteristica {
  caracteristica: string;
  es_por_tdah: boolean;
  soy_asi: boolean;
}

interface AreaImpacto {
  area: string;
  total_items: number;
  selected_count: number;
  percentage: number;
  selected_items: Array<{
    id: string;
    text: string;
  }>;
}

interface Tdah2Scores {
  caracteristicas_personales: {
    total_caracteristicas: number;
    porcentaje_tdah: number;
    porcentaje_asi: number;
    caracteristicas: Caracteristica[];
  };
  areas_impacto: {
    [key: string]: AreaImpacto;
  };
}

interface Tdah2ScoreDisplayProps {
  scores: Tdah2Scores;
  interpretations: Record<string, any>;
}

const StatCard = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <div className="relative overflow-hidden rounded-xl border border-gray-100 bg-white p-6 shadow-sm transition-all hover:shadow-md dark:border-navy-700/50 dark:bg-navy-800/50">
    <div className="flex flex-col">
      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="mt-2 text-3xl font-bold text-navy-700 dark:text-white">
        {typeof value === "number" ? `${value}%` : value}
      </p>
    </div>
  </div>
);

const Tdah2ScoreDisplay: React.FC<Tdah2ScoreDisplayProps> = ({
  scores,
  interpretations,
}) => {
  const { caracteristicas_personales, areas_impacto } = scores;

  return (
    <div className="space-y-8">
      {/* Características Personales */}
      <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm dark:border-navy-700/50 dark:bg-navy-800/50">
        <h3 className="mb-6 text-xl font-bold text-navy-700 dark:text-white">
          Características Personales
        </h3>

        <div className="mb-6 grid grid-cols-1 gap-4">
          <StatCard
            label="Total Características"
            value={caracteristicas_personales.total_caracteristicas}
          />
          <StatCard
            label="Porcentaje TDAH"
            value={caracteristicas_personales.porcentaje_tdah}
          />
          <StatCard
            label="Porcentaje 'Soy así'"
            value={caracteristicas_personales.porcentaje_asi}
          />
        </div>

        <div className="space-y-3">
          {caracteristicas_personales.caracteristicas.map(
            (item: Caracteristica, index: number) => (
              <div
                key={index}
                className="flex flex-col space-y-2 rounded-lg border border-gray-100 p-4 transition-all hover:shadow-sm dark:border-navy-700/50 dark:bg-navy-800/50"
              >
                <p className="text-base text-navy-700 dark:text-white">
                  {item.caracteristica}
                </p>
                <span
                  className={`self-start rounded-lg px-3 py-1.5 text-sm font-medium ${
                    item.es_por_tdah
                      ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400"
                      : "bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                  }`}
                >
                  {item.es_por_tdah ? "Soy asi desde que tengo recuerdo" : "Es algo nuevo en mi"}
                </span>
              </div>
            )
          )}
        </div>
      </div>

      {/* Áreas de Impacto */}
      <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm dark:border-navy-700/50 dark:bg-navy-800/50">
        <h3 className="mb-6 text-xl font-bold text-navy-700 dark:text-white">
          Áreas de Impacto
        </h3>
        <div className="grid grid-cols-1 gap-6">
          {Object.entries(areas_impacto).map(
            ([key, area]: [string, AreaImpacto]) => (
              <div
                key={key}
                className="rounded-lg border border-gray-100 p-6 transition-all hover:shadow-sm dark:border-navy-700/50 dark:bg-navy-800/50"
              >
                <div className="mb-4">
                  <div className="flex items-baseline justify-between">
                    <h4 className="text-xl font-semibold text-navy-700 dark:text-white">
                      {area.area}
                    </h4>
                    <span className="text-2xl font-bold text-navy-700 dark:text-white">
                      {area.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="mt-4 h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-700">
                    <div
                      className="h-full rounded-full bg-indigo-500 transition-all dark:bg-indigo-400"
                      style={{ width: `${area.percentage}%` }}
                    />
                  </div>
                  <p className="mt-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {area.selected_count} de {area.total_items} items
                    seleccionados
                  </p>
                </div>

                <div className="space-y-3">
                  {area.selected_items.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-lg bg-gray-50 p-4 text-base text-navy-700 dark:bg-navy-900/50 dark:text-gray-300"
                    >
                      {item.text}
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default Tdah2ScoreDisplay;
